<template>
  <TemplateLegalCenterModel
    v-if="data && data.template === 'legal'"
    v-bind="data"
  />
  <TemplateSinglePageModel
    v-else-if="data && data.template === 'single'"
    v-bind="data"
  />
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
})

// i18n - Route
defineI18nRoute({
  paths: {
    de: '/[slug]',
    en: '/[slug]',
    es: '/[slug]',
    fr: '/[slug]',
  },
})

// Composables
const { setAvailableLanguages } = useAvailableLanguages()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { meta, params } = useRoute()

// Async Data
const { data } = await useAsyncData(
  `page-[slug]-${params.slug}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/legal-center/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/single-pages/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([legalCenterJson, singlePagesJson]) => {
      if (legalCenterJson.allArticles.find(({ slug }) => slug === params.slug)) {
        const { allArticles, ...props } = legalCenterJson
        return {
          ...props,
          allArticles,
          parentPage: props.page,
          page: allArticles.find(({ slug }) => params.slug === slug),
          template: 'legal',
        }
      }
      else if (singlePagesJson.allSinglePages.find(({ slug }) => slug === params.slug)) {
        const { allSinglePages, ...props } = singlePagesJson
        return {
          ...props,
          page: allSinglePages.find(({ slug }) => params.slug === slug),
          template: 'single',
        }
      }
      else {
        return null
      }
    },
  },
)

if (!data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
  })
}

// Meta
if (data.value) {
  const { alternativeVersions } = data.value.page
  const locales = alternativeVersions.map(({ locale }) => locale)

  meta.nuxtI18n = alternativeVersions.reduce(
    (accumator, { locale, value: slug }) => ({
      ...accumator,
      [locale]: { slug },
    }),
    {},
  )

  const head = useLocaleHead({
    identifierAttribute: 'id',
    addSeoAttributes: true,
  })

  useHead({
    link: head.value.link
      .filter((link) => {
        return link.rel === 'canonical'
          || (link.rel === 'alternate' && link.hreflang === 'x-default')
          || (link.rel === 'alternate' && locales.includes(link.hreflang))
      })
      .map((link) => {
        return {
          ...link,
          href: !locales.includes('en') && link.id === 'i18n-xd'
            ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
            : link.href,
        }
      }),
    meta: head.value.meta.filter((meta) => {
      return meta.property === 'og:url'
        || meta.property === 'og:locale'
        || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
    }),
  })

  useSeoMeta({
    title: data.value.page.seoTitle,
    description: data.value.page.seoDescription,
    ogTitle: data.value.page.seoTitle,
    ogDescription: data.value.page.seoDescription,
    twitterTitle: data.value.page.seoTitle,
    twitterDescription: data.value.page.seoDescription,
  })
}

onMounted(() => {
  setAvailableLanguages(
    data.value.page.alternativeVersions.map(({ locale }) => locale),
  )
})
</script>
